import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Flex,
  HStack,
  Link,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import type { CaregiverId, PatientId } from "../../../../../shared/schema/schema";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import {
  useAgencyCalendarFirstDayOfWeek,
  usePatientProfileWeeklyTemplate,
} from "./PatientProfileWeeklyTemplateSection.hooks";
import type {
  PatientWeeklyTemplateMappedShift,
  PatientWeeklyTemplateShift,
} from "./patientWeeklyTemplate.types";
import {
  formatPatientWeeklyTemplateDay,
  formatPatientWeeklyTemplateDuration,
  formatPatientWeeklyTemplateTime,
  getPatientWeeklyTemplateDays,
  mapPatientWeeklyTemplateShifts,
} from "./patientWeeklyTemplate.utils";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patientId: PatientId;
  patientStatus?: PatientProfilePatient["status"];
};

function getCaregiverName(template: PatientWeeklyTemplateShift) {
  const caregiver = template.caregiver;

  return caregiver?.displayName ?? caregiver?.fullName ?? caregiver?.name ?? null;
}

function getNamedEntityLabel(
  value: PatientWeeklyTemplateShift["contract"] | PatientWeeklyTemplateShift["payrollCode"]
) {
  return value?.name ?? value?.displayId ?? "";
}

function getTemplateDurationMinutes(template: PatientWeeklyTemplateShift) {
  const durationLabel = formatPatientWeeklyTemplateDuration(template.startTime, template.endTime);
  const hourMatch = durationLabel.match(/^(\d+)h$/);
  const hourMinuteMatch = durationLabel.match(/^(\d+):(\d{2})h$/);

  if (hourMatch !== null) {
    return Number(hourMatch[1]) * 60;
  }

  if (hourMinuteMatch !== null) {
    return Number(hourMinuteMatch[1]) * 60 + Number(hourMinuteMatch[2]);
  }

  return 0;
}

function formatWeeklyTemplateTotalHours(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
}

function normalizeFirstDayOfWeek(value: number | string | null | undefined) {
  const parsedValue = typeof value === "string" ? Number.parseInt(value, 10) : value;

  return parsedValue !== null && parsedValue !== undefined && Number.isFinite(parsedValue)
    ? Math.max(0, Math.min(6, Math.trunc(parsedValue)))
    : 0;
}

function WeeklyTemplateSummary(props: {
  daysOfWeek: string[];
  shifts: PatientWeeklyTemplateMappedShift[];
}) {
  const templates = props.shifts.flatMap((shift) =>
    shift.dailyTemplates.filter(
      (template): template is PatientWeeklyTemplateShift => template !== null
    )
  );
  const assignedCount = templates.filter((template) => template.caregiver != null).length;
  const totalPossibleSlots = props.daysOfWeek.length * props.shifts.length;
  const emptySlots = totalPossibleSlots - templates.length;
  const totalMinutes = templates.reduce(
    (total, template) => total + getTemplateDurationMinutes(template),
    0
  );

  return (
    <Flex
      bg="#f8fafc"
      border="1px solid #e2e8f0"
      borderRadius="4px"
      gap={3}
      mb={3}
      px={3}
      py={2}
      wrap="wrap"
    >
      <Badge colorScheme="blue" textTransform="none">
        {templates.length} template shift{templates.length === 1 ? "" : "s"}
      </Badge>
      <Badge colorScheme="green" textTransform="none">
        {assignedCount} assigned
      </Badge>
      <Badge colorScheme={emptySlots > 0 ? "gray" : "green"} textTransform="none">
        {emptySlots} empty day slot{emptySlots === 1 ? "" : "s"}
      </Badge>
      <Badge colorScheme="purple" textTransform="none">
        {formatWeeklyTemplateTotalHours(totalMinutes)} weekly hours
      </Badge>
    </Flex>
  );
}

function WeeklyTemplateDayCell(props: { template: PatientWeeklyTemplateShift | null }) {
  const template = props.template;

  if (template === null) {
    return (
      <Td
        bg="#f7f7f7"
        borderColor="#ddd"
        color={entityProfileModalColors.muted}
        minW="150px"
        rowSpan={6}
        textAlign="center"
        verticalAlign="middle"
      >
        Empty day
      </Td>
    );
  }

  return (
    <Td borderColor="#ddd" minW="150px" verticalAlign="top">
      <Text color="#1f2937" fontSize="14px" fontWeight={700} whiteSpace="nowrap">
        <CalendarIcon color="#3077EB" mr={1} />
        {formatPatientWeeklyTemplateTime(template.startTime)}-
        {formatPatientWeeklyTemplateTime(template.endTime)}
      </Text>
      <Text color={entityProfileModalColors.muted} fontSize="13px">
        {formatPatientWeeklyTemplateDuration(template.startTime, template.endTime)}
      </Text>
    </Td>
  );
}

function WeeklyTemplateCaregiverCell(props: {
  actions: PatientProfileBridgeActions;
  template: PatientWeeklyTemplateShift;
}) {
  const caregiver = props.template.caregiver ?? null;
  const caregiverName = getCaregiverName(props.template);

  if (caregiver === null || caregiverName === null) {
    return (
      <Td borderColor="#ddd" minW="150px">
        None
      </Td>
    );
  }

  return (
    <Td borderColor="#ddd" minW="150px">
      <HStack alignItems="baseline" spacing={1}>
        <Link
          color="#3077EB"
          fontSize="14px"
          onClick={() => props.actions.navigateToCaregiver(caregiver.id as CaregiverId)}
        >
          {caregiverName}
        </Link>
        {caregiver.currentStatus && caregiver.currentStatus !== "ACTIVE" ? (
          <Text as="span" color="orange.500" fontSize="13px">
            ({caregiver.currentStatus})
          </Text>
        ) : null}
      </HStack>
    </Td>
  );
}

function WeeklyTemplateValueCell(props: { value: string | null | undefined }) {
  return (
    <Td borderColor="#ddd" color="#4a5568" fontSize="13px" minW="150px">
      {props.value ?? ""}
    </Td>
  );
}

function WeeklyTemplateRows(props: {
  actions: PatientProfileBridgeActions;
  daysOfWeek: string[];
  patientActive: boolean;
  shift: PatientWeeklyTemplateMappedShift;
  showShiftNumber: boolean;
}) {
  const populatedTemplates = props.shift.dailyTemplates.filter(
    (template): template is PatientWeeklyTemplateShift => template !== null
  );

  return (
    <Tbody>
      {props.showShiftNumber ? (
        <Tr>
          <Td
            bg={props.patientActive ? "#3077EB" : "#515253"}
            borderColor="#ddd"
            color="#fff"
            colSpan={props.daysOfWeek.length + 1}
            fontSize="13px"
            fontWeight={700}
            py={2}
            textAlign="center"
          >
            {props.shift.name} shift
          </Td>
        </Tr>
      ) : null}
      <Tr>
        <Td borderColor="#ddd" fontSize="13px" fontWeight={700}>
          HOURS
        </Td>
        {props.shift.dailyTemplates.map((template, index) => (
          <WeeklyTemplateDayCell
            key={`${props.shift.name}-hours-${props.daysOfWeek[index]}`}
            template={template}
          />
        ))}
      </Tr>
      <Tr>
        <Td borderColor="#ddd" fontSize="13px" fontWeight={700}>
          CAREGIVER
        </Td>
        {populatedTemplates.map((template) => (
          <WeeklyTemplateCaregiverCell
            key={`${template.dayOfWeek}-${template.id ?? template.startTime}-caregiver`}
            actions={props.actions}
            template={template}
          />
        ))}
      </Tr>
      <Tr>
        <Td borderColor="#ddd" color="#4a5568" fontSize="13px">
          Certification
        </Td>
        {populatedTemplates.map((template) => (
          <WeeklyTemplateValueCell
            key={`${template.dayOfWeek}-${template.id ?? template.startTime}-certification`}
            value={template.serviceCode?.certification}
          />
        ))}
      </Tr>
      <Tr>
        <Td borderColor="#ddd" color="#4a5568" fontSize="13px">
          Contract
        </Td>
        {populatedTemplates.map((template) => (
          <WeeklyTemplateValueCell
            key={`${template.dayOfWeek}-${template.id ?? template.startTime}-contract`}
            value={getNamedEntityLabel(template.contract)}
          />
        ))}
      </Tr>
      <Tr>
        <Td borderColor="#ddd" color="#4a5568" fontSize="13px">
          Service Code
        </Td>
        {populatedTemplates.map((template) => (
          <WeeklyTemplateValueCell
            key={`${template.dayOfWeek}-${template.id ?? template.startTime}-service-code`}
            value={getNamedEntityLabel(template.serviceCode)}
          />
        ))}
      </Tr>
      <Tr>
        <Td borderColor="#ddd" color="#4a5568" fontSize="13px">
          Pay Code
        </Td>
        {populatedTemplates.map((template) => (
          <WeeklyTemplateValueCell
            key={`${template.dayOfWeek}-${template.id ?? template.startTime}-pay-code`}
            value={getNamedEntityLabel(template.payrollCode)}
          />
        ))}
      </Tr>
    </Tbody>
  );
}

export default function PatientProfileWeeklyTemplateSection(props: Props) {
  const weeklyTemplateQuery = usePatientProfileWeeklyTemplate(props.patientId);
  const firstDayOfWeekQuery = useAgencyCalendarFirstDayOfWeek();
  const firstDayOfWeek = normalizeFirstDayOfWeek(
    firstDayOfWeekQuery.data?.calendarStartDayOfTheWeek
  );
  const daysOfWeek = getPatientWeeklyTemplateDays(firstDayOfWeek);
  const shifts = mapPatientWeeklyTemplateShifts(weeklyTemplateQuery.data?.shifts ?? [], daysOfWeek);

  if (weeklyTemplateQuery.isLoading) {
    return (
      <Alert bg="#f8fafc" border="1px solid #e2e8f0" borderRadius="4px" status="info">
        <Spinner mr={3} size="sm" />
        <Stack spacing={0}>
          <AlertTitle fontSize="14px">Loading weekly template</AlertTitle>
          <AlertDescription color={entityProfileModalColors.muted} fontSize="13px">
            Fetching the patient&apos;s recurring shift pattern.
          </AlertDescription>
        </Stack>
      </Alert>
    );
  }

  if (weeklyTemplateQuery.isError) {
    return (
      <Alert borderRadius="4px" status="error">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle fontSize="14px">Unable to load weekly template.</AlertTitle>
          <AlertDescription fontSize="13px">
            Retry to reload the recurring schedule for this patient.
          </AlertDescription>
        </Box>
        <EntityModalButton
          kind="white"
          size="sm"
          onClick={() => void weeklyTemplateQuery.refetch()}
        >
          Retry
        </EntityModalButton>
      </Alert>
    );
  }

  if (shifts.length === 0) {
    return (
      <Alert borderRadius="4px" status={props.patientStatus === "ACTIVE" ? "warning" : "info"}>
        <AlertIcon />
        <Stack spacing={1}>
          <HStack spacing={2}>
            <AlertTitle fontSize="14px">No weekly template shifts found.</AlertTitle>
            {props.patientStatus === "ACTIVE" ? (
              <Badge colorScheme="red" textTransform="none">
                Missing weekly template
              </Badge>
            ) : null}
          </HStack>
          <AlertDescription fontSize="13px">
            {props.patientStatus === "ACTIVE"
              ? "Active patients without recurring shifts are marked as missing a weekly template."
              : "This patient does not currently have recurring weekly template shifts."}
          </AlertDescription>
        </Stack>
      </Alert>
    );
  }

  return (
    <Box overflowX="auto">
      <WeeklyTemplateSummary daysOfWeek={daysOfWeek} shifts={shifts} />
      <Table border="1px solid #ddd" size="sm" variant="simple">
        <Thead>
          <Tr bg={props.patientStatus === "ACTIVE" ? "#3077EB" : "#515253"}>
            <Th borderColor="#ddd" color="#fff" fontSize="12px" minW="145px">
              DAYS OF WEEK
            </Th>
            {daysOfWeek.map((dayOfWeek) => (
              <Th key={dayOfWeek} borderColor="#ddd" color="#fff" fontSize="12px" minW="150px">
                {formatPatientWeeklyTemplateDay(dayOfWeek)}
              </Th>
            ))}
          </Tr>
        </Thead>
        {shifts.map((shift) => (
          <WeeklyTemplateRows
            key={shift.name}
            actions={props.actions}
            daysOfWeek={daysOfWeek}
            patientActive={props.patientStatus === "ACTIVE"}
            shift={shift}
            showShiftNumber={shifts.length > 1}
          />
        ))}
      </Table>
    </Box>
  );
}
